import { For, Show, createMemo, createSignal, createUniqueId } from "solid-js";
import { Button } from "./Form.tsx";
import { ChevronDownIcon } from "./icons.tsx";
import { useDismiss } from "../lib/popover.ts";
import styles from "./CheckMenu.module.scss";

export type CheckMenuOption = {
  id: string;
  label: string;
  /** Names the section this option belongs to; absent leaves it ungrouped. */
  group?: string;
};

export type CheckMenuProps = {
  label: string;
  options: readonly CheckMenuOption[];
  picked: readonly string[];
  /** Emits in option order, so what is stored never depends on click order. */
  onChange: (picked: string[]) => void;
  allLabel: string;
  noneLabel: string;
  emptyLabel: string;
};

/**
 * Many-of-many behind one button.
 *
 * A `CheckList` says the same thing inline, which is right in a form; here the
 * list is a filter over a chart and would otherwise take more room than the
 * chart itself. Not a `MenuButton`, because these are a value the reader keeps
 * editing rather than commands they fire once.
 */
export const CheckMenu = (props: CheckMenuProps) => {
  const [open, setOpen] = createSignal(false);
  const labelId = createUniqueId();
  let root!: HTMLDivElement;

  useDismiss(() => root, () => setOpen(false));

  const picked = createMemo(() => new Set(props.picked));

  const emit = (next: Set<string>) =>
    props.onChange(props.options.filter((option) => next.has(option.id)).map((option) => option.id));

  const toggle = (id: string, on: boolean) => {
    const next = new Set(picked());
    if (on) next.add(id);
    else next.delete(id);
    emit(next);
  };

  const groups = createMemo(() => {
    const named = new Map<string, CheckMenuOption[]>();
    for (const option of props.options) {
      const key = option.group ?? "";
      named.set(key, [...(named.get(key) ?? []), option]);
    }
    return [...named.entries()].map(([group, options]) => ({ group, options }));
  });

  return (
    <div class={styles.menu} ref={root}>
      <Button
        label={`${props.label} (${props.picked.length}/${props.options.length})`}
        icon={<ChevronDownIcon />}
        onClick={() => setOpen(!open())}
      />
      <Show when={open()}>
        <div class={styles.panel} role="group" aria-labelledby={labelId}>
          <span class={styles.heading} id={labelId}>
            {props.label}
          </span>
          <div class={styles.bulk}>
            <button
              class={styles.bulkAction}
              type="button"
              onClick={() => emit(new Set(props.options.map((option) => option.id)))}
            >
              {props.allLabel}
            </button>
            <button class={styles.bulkAction} type="button" onClick={() => emit(new Set())}>
              {props.noneLabel}
            </button>
          </div>
          <Show
            when={props.options.length > 0}
            fallback={<span class={styles.empty}>{props.emptyLabel}</span>}
          >
            <div class={styles.list}>
              <For each={groups()}>
                {(entry) => (
                  <>
                    <Show when={entry.group !== ""}>
                      <span class={styles.group}>{entry.group}</span>
                    </Show>
                    <For each={entry.options}>
                      {(option) => (
                        <label class={styles.option}>
                          <input
                            type="checkbox"
                            checked={picked().has(option.id)}
                            onChange={(event) => toggle(option.id, event.currentTarget.checked)}
                          />
                          <span>{option.label}</span>
                        </label>
                      )}
                    </For>
                  </>
                )}
              </For>
            </div>
          </Show>
        </div>
      </Show>
    </div>
  );
};
